import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { Lottery } from 'src/util/types';
import config from '../../../config';
import { useAuth } from 'src/util/authenticationProvider';
import LotteryView from 'src/components/LotteryView';

type PublicChannel = {
  id: string;
  name: string;
  lotteryId?: string;
};

export default function ChannelViewPage() {
  const { name } = useParams<{ name: string }>();
  const normalizedChannelName = useMemo(() => (name ? name.toLowerCase() : ''), [name]);
  const { tokens, login, refresh } = useAuth();
  const headers = useMemo(() => (tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : undefined), [tokens?.accessToken]);
  const refreshTimer = useRef<number | null>(null);

  const [lottery, setLottery] = useState<Lottery | null>(null);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(!headers);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const fetchLotteryForChannel = useCallback(async () => {
    if (!normalizedChannelName || !headers) {
      setLottery(null);
      setGalleryImages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const channelsRes = await axios.get(`${config.apiUrl}/api/channels/all`, {
        withCredentials: true,
        headers
      });
      const payload = channelsRes?.data?.content ?? channelsRes?.data ?? [];
      const arr = Array.isArray(payload) ? payload : [];
      const channel = arr.find((item: PublicChannel) => (item?.name ?? '').toLowerCase() === normalizedChannelName);
      const lotteryIdentifier = channel?.lotteryDto?.id ?? channel?.lotteryId;
      if (!lotteryIdentifier) {
        setError('please assign a valid lottery to this channel');
        setLottery(null);
        setGalleryImages([]);
        setLoading(false);
        return;
      }

      const lotteryRes = await axios.get(`${config.apiUrl}/api/lottery/${encodeURIComponent(lotteryIdentifier)}`, {
        headers
      });
      const lotteryData = lotteryRes?.data?.content ?? lotteryRes?.data;
      setLottery(!lotteryData || lotteryData.closed ? null : lotteryData);

      try {
        const galleryRes = await axios.get(`${config.apiUrl}/api/media/all`, {
          headers,
          params: { category: 'gallery' }
        });
        if (galleryRes?.data?.status >= 200 && Array.isArray(galleryRes.data.content)) {
          const images = galleryRes.data.content.map((media: any) => ({
            src: media.image,
            title: media.title,
            id: media.id
          }));
          setGalleryImages(images);
        } else {
          setGalleryImages([]);
        }
      } catch {
        setGalleryImages([]);
      }
    } catch {
      setError('please assign an active lottery without errors');
      setLottery(null);
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  }, [headers, normalizedChannelName]);

  useEffect(() => {
    if (headers) {
      setLoginDialogOpen(false);
      fetchLotteryForChannel();
    } else {
      setLoginDialogOpen(true);
      setLottery(null);
      setGalleryImages([]);
      setLoading(true);
    }
  }, [fetchLotteryForChannel, headers]);

  useEffect(() => {
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    if (!tokens?.expiresAt || !headers) return;
    const delay = Math.max(tokens.expiresAt - Date.now() - 10_000, 5_000);
    refreshTimer.current = window.setTimeout(async () => {
      try {
        await refresh();
        await fetchLotteryForChannel();
      } catch {
        setLoginDialogOpen(true);
      }
    }, delay);
    return () => {
      if (refreshTimer.current) {
        window.clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
    };
  }, [fetchLotteryForChannel, headers, refresh, tokens?.expiresAt]);

  useEffect(() => {
    if (!headers) return;
    const intervalId = window.setInterval(fetchLotteryForChannel, 60_000);
    return () => window.clearInterval(intervalId);
  }, [fetchLotteryForChannel, headers]);

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    setLoginError(null);
    setLoginLoading(true);
    try {
      await login(username.trim(), password.trim());
      setLoginDialogOpen(false);
      await fetchLotteryForChannel();
    } catch (err: any) {
      setLoginError(err?.message || 'Login fehlgeschlagen');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <>
      <LotteryView lottery={lottery} galleryImages={galleryImages} loading={loading} error={error} variant="channel" />
      <Dialog open={loginDialogOpen} fullWidth maxWidth="xs">
        <form onSubmit={handleLogin}>
          <DialogTitle>Bitte anmelden</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} mt={1}>
              <TextField label="Benutzername" value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus />
              <TextField label="Passwort" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              {loginError && <Alert severity="error">{loginError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button type="submit" variant="contained" disabled={loginLoading}>
              {loginLoading ? 'Wird angemeldet...' : 'Login'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
